import type { OfferingModel } from '@sosocio/creator-types/database';
import type {
	PageObjectModel,
	PhotoModel,
	ProductSettings,
} from '@sosocio/creator-types/project';
import { ConfigModule } from 'store';
import maxObjectSize from 'tools/max-object-size';

export const LOW_RESOLUTION_WARNING_ICON_OFFSET = 8;
export const LOW_RESOLUTION_WARNING_ICON_SIZE_DESKTOP = 32;
export const LOW_RESOLUTION_WARNING_ICON_SIZE_MOBILE = 20;

export type PhotoQualityWarningType = 'maxPhotoSize' | 'lowResolution';

type PhotoWarningObjectModel = OptionalExceptFor<PageObjectModel, 'type' | 'height' | 'width'>;

interface PhotoWarningSizeModel {
	height: PhotoModel['full_height'];
	width: PhotoModel['full_width'];
}

interface PhotoWarningTypeOptions {
	objectModel: PhotoWarningObjectModel;
	offeringModel?: OfferingModel | null;
	photoData?: PhotoWarningSizeModel;
}

/**
 * Resolves the warning icon size based on the viewport mode.
 *
 * @param isMobile Indicates whether the warning is rendered in mobile mode.
 * @returns Icon size in pixels.
 */
export function getLowResolutionWarningIconSize(isMobile: boolean): number {
	return (
		isMobile
			? LOW_RESOLUTION_WARNING_ICON_SIZE_MOBILE
			: LOW_RESOLUTION_WARNING_ICON_SIZE_DESKTOP
	);
}

/**
 * Resolves the photo quality warning type for the provided object.
 *
 * @param options Object, photo, and offering data used to evaluate warning thresholds.
 * @returns The warning type when applicable, otherwise null.
 */
export function getPhotoQualityWarningType(options: PhotoWarningTypeOptions): PhotoQualityWarningType | null {
	if (
		options.objectModel.type !== 'photo'
		|| !options.objectModel.photoid
		|| options.objectModel.ext === 'svg'
	) {
		return null;
	}

	const maxsize = maxObjectSize(
		{
			maxwidth: options.objectModel.maxwidth,
			maxheight: options.objectModel.maxheight,
			cropwidth: options.objectModel.cropwidth,
			cropheight: options.objectModel.cropheight,
			type: options.objectModel.type,
		},
		options.photoData,
		options.offeringModel ?? undefined,
	);

	if (
		Math.round(options.objectModel.width) >= Math.round(maxsize.cropWidth)
		|| Math.round(options.objectModel.height) >= Math.round(maxsize.cropHeight)
	) {
		return 'maxPhotoSize';
	}

	if (
		Math.round(options.objectModel.width) > Math.round(maxsize.croppedQualityWidth)
		|| Math.round(options.objectModel.height) > Math.round(maxsize.croppedQualityHeight)
	) {
		return 'lowResolution';
	}

	return null;
}

/**
 * Determines whether the provided photo object should display a low-resolution warning.
 *
 * @param options Object, photo, and offering data used to evaluate warning thresholds.
 * @returns True when the object is low-resolution.
 */
export function isLowResolutionPhotoObject(options: PhotoWarningTypeOptions): boolean {
	return getPhotoQualityWarningType(options) !== null;
}

/**
 * Determines whether upscaling should be applied based on offering and product settings.
 *
 * @param offeringModel {@link OfferingModel} context used to evaluate upscaling behavior.
 * @param productSettings {@link ProductSettings} context used to evaluate upscaling behavior.
 * @returns True when upscaling should be applied.
 */
export function shouldApplyUpscaling(
	offeringModel?: OfferingModel | null,
	productSettings?: ProductSettings,
): boolean {
	return !!(
		offeringModel?.applyUpscaling
		&& productSettings?.applyEnhancement !== false
	);
}

/**
 * Determines whether vectorization should be applied based on offering settings.
 *
 * @param offeringModel {@link OfferingModel} context used to evaluate vectorization behavior.
 * @returns True when vectorization should be applied.
 */
export function shouldApplyVectorization(offeringModel?: OfferingModel | null): boolean {
	return !!(
		offeringModel?.applyVectorization
		&& ConfigModule['features.vectorization']
	);
}
